import type {Block, BlockProperties, Component, ComponentDefinition, Editor} from 'grapesjs';
import {EMAIL_MAKER_BLOCKS, EMAIL_MAKER_COMPONENT_TYPES, MAX_HTML_CONTENT_LENGTH} from '@EmailMakerReact/email-maker-react.constants';
import {getInlinedHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {renderEmailMakerBlockManager} from '@EmailMakerReact/lib/email-maker-block-manager-renderer';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';

const SELECT_AFTER_APPEND_DELAY_MS = 50;

interface EmailMakerBlockAttributes extends BlockProperties {
  trackingEvent?: string;
}

/** Block click handler: appends next to the current selection when possible, walking up to the content container, with a template-size guard. */
export function appendBlockOnClick(editor: Editor, block: Block, insertAtPosition: number | null = null): Component | undefined {
  // Boundary cast: blocks in this editor only carry string or single-definition content.
  const content = block.get('content') as ComponentDefinition | string;
  const selected = editor.getSelected();
  let target: Component | undefined;
  let valid: boolean;
  let insertAt: number | undefined;

  const htmlContent = getInlinedHtml(editor);

  if (htmlContent.length > MAX_HTML_CONTENT_LENGTH) {
    return undefined;
  }

  // Boundary cast: trackingEvent is a PMW custom block model prop the GrapesJS typings don't know about.
  const trackingEvent = (block.attributes as EmailMakerBlockAttributes).trackingEvent;
  if (trackingEvent) {
    window.PMW.gtm.trackGA4CustomEvent(trackingEvent);
  }

  if (selected) {
    valid = editor.Components.canMove(selected, content).result;

    if (valid) {
      target = selected;
    } else {
      let parent = selected.parent();

      if (!parent) {
        const wrapperComponents = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER);
        parent = wrapperComponents?.[0];
      }

      if (parent) {
        valid = editor.Components.canMove(parent, content).result;
      }
      if (valid && parent) {
        target = parent;
        insertAt = parent.components().indexOf(selected) + 1;
      }
    }

    if (!target && getContentType(content) === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION) {
      let current: Component | null = selected;
      let child: Component | null = null;
      let contentContainer: Component | null = null;

      while (current) {
        if (current.get('type') === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER) {
          contentContainer = current;
          break;
        }
        child = current;
        current = current.parent() ?? null;
      }

      valid = contentContainer ? editor.Components.canMove(contentContainer, content).result : false;

      if (valid && contentContainer && child) {
        target = contentContainer;
        insertAt = target.components().indexOf(child) + 1;
      }
    }
  }

  if (!target) {
    const contentContainers = editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT_CONTAINER);
    const contentContainer = contentContainers?.[0];
    if (contentContainer) {
      valid = editor.Components.canMove(contentContainer, content).result;
      if (valid) {
        target = contentContainer;
      }
    }
  }

  if (insertAtPosition !== null) {
    insertAt = insertAtPosition;
  }

  const result = target?.append(content, {at: insertAt})[0];
  const toActive = block.get('activate');
  const toSelect = block.get('select');

  if (result) {
    if (toSelect || (toActive && toSelect !== false)) {
      scheduleSelectAfterAppend(editor, result);
    }

    if (toActive) {
      result.trigger('active');
    }

    if (block.get('resetId')) {
      result.onAll((component: Component) => component.resetId());
    }
  }

  return result;
}

/** Delays selection so media components render first for accurate scroll positioning; bails if the editor was torn down since the append. */
function scheduleSelectAfterAppend(editor: Editor, result: Component): void {
  setTimeout(() => {
    if (editor.getWrapper()) {
      editor.select(result, {scroll: true});
    }
  }, SELECT_AFTER_APPEND_DELAY_MS);
}

/** Opens the add-blocks modal. The block-manager container is reused across opens (destroy-on-close=false). */
export function openAddBlocksDialog(editor: Editor): void {
  const dialog = window.PMW.openModalDialog(EMAIL_MAKER_BLOCKS.ID_ADD_BLOCKS_DIALOG, window.i18next.t('pmwjs_add_to_email'), null, false, '-modal-100');

  let blocksManagerContainer = document.getElementById(EMAIL_MAKER_BLOCKS.ID_BLOCKS_MANAGER_CONTAINER);

  if (!blocksManagerContainer) {
    blocksManagerContainer = document.createElement('div');
    blocksManagerContainer.id = EMAIL_MAKER_BLOCKS.ID_BLOCKS_MANAGER_CONTAINER;

    renderEmailMakerBlockManager(blocksManagerContainer, editor);
  }

  dialog[0]?.querySelector('.modal-content')?.replaceChildren(blocksManagerContainer);
}

/** Closes the add-blocks dialog and the React mobile block sheet. */
export function closeAddBlocksDialog(): void {
  void window.PMW.closeModalDialog(EMAIL_MAKER_BLOCKS.ID_ADD_BLOCKS_DIALOG);
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_BLOCK_MANAGER_MODAL));
}

function getContentType(content: ComponentDefinition | string): string | undefined {
  if (typeof content !== 'object') {
    return undefined;
  }

  const {type} = content;
  return typeof type === 'string' ? type : undefined;
}
